import {
	createContext,
	useContext,
	ReactNode,
	ReactElement,
	useMemo,
	useState,
	useEffect,
	useRef
} from 'react';
import assert from 'assert';
import { Task, User } from '@techshift/callie-private-api-typescript';
import { useOpenApi } from '../../core/providers';
import { usePrevious, useWebsocket } from '@techshift/react-core';

export interface TasksContextData {
	task: Task;
	user: User;
	isAlerted: boolean;
}

export const TasksContext = createContext<TasksContextData | undefined>(
	undefined
);

type TasksProvderProps = {
	task: Task;
	user: User;
	readonly children?: ReactNode;
};
export function TasksProvider(props: TasksProvderProps): ReactElement {
	const { task: _task, user: _user, children } = props;

	const [loading, setLoading] = useState(false);
	const [task, setTask] = useState<Task>(_task);
	const [user, setUser] = useState<User>(_user);

	const isAlerted: boolean = useMemo(
		() => (task.alertType && task.status === 'open' ? true : false),
		[task.alertType, task.status]
	);

	const { openApi } = useOpenApi();
	const openApiRef = useRef(openApi);
	openApiRef.current = openApi;

	const { subscribe, unsubscribe, on, off, authenticated } = useWebsocket();
	const prevAuthenticated = usePrevious(authenticated);

	/**
	 * Fetch data
	 */
	useEffect(() => {
		if (loading) {
			return;
		}

		let shouldFetch = false;

		// Always fetch if the socket has turned off and back on again (As we have a period of time where we don't get updates)
		if (authenticated && !prevAuthenticated) {
			shouldFetch = true;
		}

		if (!shouldFetch) {
			return;
		}

		function fetch() {
			return openApiRef.current.tasks
				.getTask(task.id)
				.then((response) => {
					setTask(response.data.data.task);
					return openApiRef.current.users.getUser(
						response.data.data.task.userId
					);
				})
				.then((repsonse) => {
					setUser(repsonse.data.data.user);
				})
				.catch(() => {
					// Wait 5 seconds before retrying
					return new Promise((resolve) => {
						setTimeout(() => {
							resolve(fetch());
						}, 5000);
					});
				});
		}

		setLoading(true);
		fetch().finally(() => {
			setLoading(false);
		});
	}, [authenticated, loading, prevAuthenticated, task.id]);

	/**
	 * Websockets
	 */
	useEffect(() => {
		const ids = subscribe([
			{ resource: `tasks#${task.id}` },
			{ resource: `users#${task.userId}` }
		]);
		return () => {
			unsubscribe(ids);
		};
	}, [task.id, task.userId, subscribe, unsubscribe]);

	useEffect(() => {
		const tasksCallback = (message: any) => {
			if (message.data.task.id === task.id) {
				setTask(message.data.task);
			}
		};
		const usersCallback = (message: any) => {
			if (message.data.user.id === task.userId) {
				setUser(message.data.user);
			}
		};
		on('tasks', tasksCallback);
		on('users', usersCallback);

		return () => {
			off('tasks', tasksCallback);
			off('users', usersCallback);
		};
	}, [on, off, setTask, task.id, task.userId]);

	return (
		<TasksContext.Provider
			value={{
				task,
				user,
				isAlerted
			}}
		>
			{children}
		</TasksContext.Provider>
	);
}

export function useTasks(): TasksContextData {
	const data = useContext<TasksContextData | undefined>(TasksContext);

	assert(
		data,
		"Tasks unavailable. Are you sure you're using this in a nested component of an TasksProvider?"
	);

	return data;
}
